(ns frontend.view.nev.kyc.process-status-views.waiting-for-esign-status
    (:require-macros [frontend.macros :refer [without-default-action-and-propagation]])
    (:require
     [common.localization :as loc]
     [frontend.state.current-session :as session]
     [frontend.actions.kyc-process :as kyc-process-actions]
     [frontend.logging :as logging]
     [common.languages :as languages]
     [frontend.view.nev.kyc.process-status-views.print-view :as print-view]
     [common.roles :as roles]
     [frontend.utils :as fu]
     [frontend.view.modals.events :as modals]
     [frontend.view.modals.delete-confirmation.view :as delete-confirmation-modal]
     [frontend.view.modals.error-message.view :as error-message-modal]
     [frontend.actions.entity :as entity-actions]
     [frontend.view.entity-viewers.modules.attachments :as attachments]
     [frontend.view.nev.kyc.process-status-views.view-utils :as kvu]
     [common.util :as cu]
     [clojure.string :as string]
     [frontend.view.nev.kyc.process.pdf-make-utils :as pdf-make-utils]
     [frontend.actions.attachment :as attachment-actions]
     [frontend.actions.common :as common-actions]
     [ui.components.button.view :as button]))

(defn create-default-kyc-pdf-filename
  [{:keys [answers now-date]}]
  (let [investor-name (or (let [first-name (:kyc-person/first-name answers)
                                last-name (:kyc-person/last-name answers)
                                full-name (string/trim (str first-name " " last-name))]
                            (when-not (cu/blank-value? full-name)
                              full-name))

                          (let [target-company-name (-> answers
                                                        :kyc-legal-entity/target-company-name
                                                        cu/str-trim)]
                            (when-not (cu/blank-value? target-company-name)
                              target-company-name))

                          (let [tenant-name (-> answers
                                                :kyc-tenant/name
                                                cu/str-trim)]
                            (when-not (cu/blank-value? tenant-name)
                              tenant-name))

                          (let [contract-company-name (-> answers
                                                          :contract-kyc/business-name
                                                          cu/str-trim)]
                            (when-not (cu/blank-value? contract-company-name)
                              contract-company-name))

                          (let [legal-entity-name (-> answers
                                                      :kyc-legal-entity/name
                                                      cu/str-trim)]
                            (when-not (cu/blank-value? legal-entity-name)
                              legal-entity-name)))
        date-str (-> now-date
                     (.toISOString)
                     (.split "T")
                     (aget 0))
        filename (str investor-name " KYC " date-str " unsigned.pdf")]
    filename))

(defn upload-pdf
  [file kyc-process-id success!]
  (let [attachment {:filename    (.-name file)
                    :description nil
                    :file        file
                    :target      kyc-process-id}
        token-success! (fn [token]
                         (attachment-actions/*=>add-attachment
                           {:multipart-params (assoc attachment :token token)}
                           (fn [resp]
                             (when success! (success! (:body resp))))
                           (fn [e]
                             (logging/error "Error in uploading attachment" e))))]
    (common-actions/*<-create-op-token nil
                                       token-success!
                                       (fn [e]
                                         (logging/error "resp token=" e)))))

(defn complete!
  [entity answers-atom state-atom attachment-id save!]
  (kyc-process-actions/*->complete!
    {:kyc-process-id (:db/id entity)
     :attachment-id  attachment-id}
    (fn [_resp]
      (swap! answers-atom dissoc :kyc-legal-entity/signature-date)
      (deref answers-atom)
      (save!)
      (swap! state-atom assoc :loading? false))
    (fn [resp]
      (swap! state-atom assoc :loading? false)
      (let [lang (or (:lang @state-atom) (:kyc-process/language entity))
            response-message (let [msg (:message resp)]
                               (if (keyword? msg) (name msg) msg))
            error-message (case response-message
                            "invalid-signatory-emails" (loc/tr :invalid-emails nil lang)
                            "missing-signer-email" (loc/tr :missing-signer-email nil lang)
                            (loc/tr :sending-e-signature-request-failed nil lang))]
        (logging/error "Failed to complete KYC " resp)
        (modals/open-modal!
         error-message-modal/modal-key
         {:title          (loc/tr :attention nil lang)
          :body-content   error-message
          :ok-button-text (loc/tr :ok nil lang)})))))

(defn e-sign-printout-view
  [state-atom local-atom entity save!]
  (let [lang (or (:lang @state-atom) (:kyc-process/language entity))
        lang-kw (languages/long->short lang)]
    ;(fu/timeout 1000 #(show-as-pdf! state-atom local-atom entity))
    [print-view/print-hiccup
     {:state-atom      state-atom
      :save!           save!
      :entity          entity
      :mandatory?      false
      :read-only?      true
      :lang            lang
      :footer-renderer (fn [state-atom answers-atom entity]
                         [:div.d-flex.flex-static.flex-1.flex-column.kyc-footer-50
                          [:div.container
                           [:ol
                            {:class "ps-4 pb-4 flex flex-col gap-3"}
                            [:li
                             [:div
                              {:class "flex flex-col gap-2"}
                              (if (= lang :FIN)
                                (str "Tarkista lomake ja lähetä eSignattavaksi")
                                (str "Review the submitted data, generate pdf and start eSign process"))
                              [:div.mt-1.mb-1
                               [:label.mr-1
                                (if (= lang :FIN)
                                  "Allekirjoittajien sähköpostiosoitteet: "
                                  "Signers' email addresses: ")]
                               (->> @answers-atom
                                    (keep (fn [[k v]]
                                            (when (string/starts-with? (name k) "signer-email")
                                              (when-not (cu/blank-value? v)
                                                (cu/str-trim-lcase v)))))
                                    (map-indexed (fn [idx email]
                                                   [:strong.mr-1
                                                    {:key (str "Signer-" email "-" idx)}
                                                    email]))
                                    (doall))]]]

                            [:li
                             (if (= lang :FIN)
                               (str "Lataa allekirjoitettu dokumentti allaolevalla formilla")
                               (str "Upload signed document using the form below"))]]

                           [:div
                            {:class "flex flex-col gap-3"}
                            [:div
                             {:class "flex gap-3"}
                             [button/sm-primary-solid
                              {:on-click (fn []
                                           (pdf-make-utils/save-as-pdf! {:entity entity
                                                                         :state-atom state-atom}))}
                              (loc/tr :save-as-pdf nil lang)]
                             [button/sm-secondary-hollow
                              {:on-click (fn [_]
                                           (kyc-process-actions/*->re-edit-form! {:kyc-process-id (:db/id entity)}
                                                                                 (fn [_resp])
                                                                                 (fn [resp]
                                                                                   (logging/error "Failed to complete KYC " resp))))}
                              (loc/tr :edit-form nil lang)]
                             [button/sm-secondary-hollow
                              {:on-click (fn [_]
                                           (swap! state-atom assoc :loading? true)
                                           (pdf-make-utils/pdf-blob
                                             {:entity entity
                                              :state-atom state-atom
                                              :e-sign-version? true
                                              :callback-fn (fn [blob]
                                                             (let [file-name (case (:kyc-process/template entity)
                                                                               :kyc-template/innovestor-cofund-angel-entity
                                                                               (loc/tr :kyc-template-pdf-name-entity nil lang)
                                                                               :kyc-template/innovestor-cofund-angel-individual
                                                                               (loc/tr :kyc-template-pdf-name-individual nil lang)
                                                                               :kyc-template/innovestor-cofund-angel-target-company
                                                                               (loc/tr :kyc-template-pdf-name-target-company nil lang)
                                                                               (create-default-kyc-pdf-filename
                                                                                 {:answers @answers-atom
                                                                                  :now-date (js/Date.)}))
                                                                   file (js/File. (array blob) file-name)
                                                                   kyc-process-id (:db/id entity)
                                                                   success! (fn [attachment]
                                                                              (complete! entity answers-atom state-atom
                                                                                         (:db/id attachment) save!))]
                                                               (upload-pdf file kyc-process-id success!)))}))
                               :disabled (:loading? @state-atom)}
                              (if (:loading? @state-atom) (loc/tr :sending nil lang) (loc/tr :kyc-send-for-esign nil lang))]]

                            [:div.mt-3
                             [:label.input-editor__label.pb-2 (loc/tr :kyc-signatures-file nil lang)]
                             [:div.mt-2.mb-3
                              (if-let [signed-document-id (:kyc-process/signed-document entity)]
                                (let [attachment (get @session/entities signed-document-id)]
                                  [:div.attachments-list-attachment
                                   (when (roles/user? @session/role)
                                     {:on-click #(fu/open-attachment! signed-document-id)})
                                   [:div.module-attachment-left
                                    [:i.fal.fa-paperclip.module-attachment-icon]
                                    [:span.module-attachment-filename (:attachment/filename attachment)]]
                                   [:div.module-attachment-right
                                    [:div.module-attachment-buttons
                                     (when (roles/user? @session/role)
                                       [:button.module-attachment-button
                                        {:on-click (without-default-action-and-propagation
                                                     (fu/open-attachment! (:db/id attachment)))}
                                        [:i.fal.fa-external-link]])
                                     (when (roles/user? @session/role)
                                       [:button.module-attachment-button
                                        {:on-click (without-default-action-and-propagation
                                                     (fu/download-attachment! (:db/id attachment)))}
                                        [:i.fal.fa-cloud-download]])
                                     [:button.module-attachment-button
                                      {:on-click (without-default-action-and-propagation
                                                   (modals/open-modal!
                                                     delete-confirmation-modal/modal-key
                                                     {:body       (loc/tr :attachment-delete nil lang-kw)
                                                      :lang-kw    lang-kw
                                                      :on-delete! (fn [_]
                                                                    (entity-actions/*->field-update! (:db/id entity)
                                                                                                     :kyc-process/signed-document
                                                                                                     nil
                                                                                                     (fn [_resp])
                                                                                                     (fn [err]
                                                                                                       (logging/error
                                                                                                         "Failed to remove signed document "
                                                                                                         err))))}))}
                                      [:i.fal.fa-trash-alt.pointer]]]]])
                                (if (:uploading @local-atom)
                                  [attachments/indicators local-atom (:db/id entity)]
                                  [attachments/upload-area local-atom (:db/id entity)
                                   (fn [resp]
                                     (kyc-process-actions/*->uploaded-signed-document {:kyc-process-id (:db/id entity)
                                                                                       :attachment-id  (:db/id resp)}
                                                                                      (fn [_resp]
                                                                                        (swap! local-atom dissoc :uploading))
                                                                                      (fn [err]
                                                                                        (logging/error "Failed to submit PDF " err))))
                                   {:lang-kw lang-kw
                                    :accept  ".pdf"}]))]]
                            [:div
                             {:class "flex gap-3"}
                             [kvu/exit-button entity lang-kw]
                             [button/primary-solid
                              {:on-click (fn [_]
                                           (kyc-process-actions/*->complete! {:kyc-process-id (:db/id entity)}
                                                                             (fn [_resp])
                                                                             (fn [resp]
                                                                               (logging/error "Failed to complete KYC " resp))))}
                              (loc/tr :submit nil lang)]]]]])}]))

(defn view
  [state-atom local-atom entity save!]
  (if (or (session/kyc?)
          (session/tenant-kyc?)
          (session/contract-kyc?))
    [e-sign-printout-view state-atom local-atom entity save!]
    (let [lang (:kyc-process/language entity)]
      [:div.container.text-center
       [:h1.mt-4
        (if (= lang :FIN)
          "Kiitos, lomake on lähetetty"
          "Thank you, the form has been submitted!")]
       [:p.mt-4
        (if (= lang :FIN)
          (str "Tarkistamme lomakkeen ja lähetämme allekirjoittajille linkin sähköiseen"
               " allekirjoitukseen kahden työpäivän kuluessa.")
          (str "We will review the submitted data and send eSign links by email to"
               " the signers' email addresses. The review process may take up to 2 working days"))]
       [:p.mt-4
        (if (= lang :FIN)
          (str "Voit sulkea selaimen / välilehden.")
          (str "You can now close the browser / tab."))]
       [:div
        {:class "pt-4 flex justify-center"}
        [button/secondary-hollow
         {:on-click (fn [_]
                      (kyc-process-actions/*->re-edit-form! {:kyc-process-id (:db/id entity)}
                                                            (fn [_resp])
                                                            (fn [resp]
                                                              (logging/error "Failed to complete KYC " resp))))}
         (loc/tr :cancel-submission nil lang)]]])))
